/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 * NavbarService layer between component and Content provider service to handle all filtering
 * and data manipulation logic for global navigation data
 */
import { Injectable } from '@angular/core';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { FlowInfo, LineWorkflow, Workflow } from '@root/shared/interfaces/workflow.interface';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { Observable } from 'rxjs';
import { WorkflowCartSupportService } from '@root/shared/services/workflow-cart-support.service';
import { map } from 'rxjs/operators';
@Injectable()
export class WorkflowService {
  public workflowInformation: Workflow[];
  public firstTileObject: LineWorkflow;
  public workflowMap: LineWorkflow[];
  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private contentProviderService: ContentProviderService,
    private workflowCartSupportService: WorkflowCartSupportService
  ) {}

  /**
   * setWorkflowIdOnRouteChange method is used to set the workflow id on route change
   * @param url of the current route
   * @returns void
   */
  public setWorkflowIdOnRouteChange(url: string): void {
    this.stateStoreUtilsService.setCurrentRoute(url);
    const selectedRoute: Workflow = this.getSelectedRoute(url);
    this.updateWorkFlowId(selectedRoute, 'Default');
  }

  /**
   * setWorkflowIdOnEvent method is used to set/check the modified workflow id on any event
   * @param type of type string, having type of workflow
   * @returns void
   */
  public setWorkflowIdOnEvent(type: string): void {
    const url = this.stateStoreUtilsService.getCurrentRoute();
    const selectedRoute: Workflow = this.getSelectedRoute(url);
    this.updateWorkFlowId(selectedRoute, type);
  }

  /**
   * removeWorkflowId method is used to remove current workflow id from headers
   * @param flowName of type string, having flow name of repdash
   * @returns void
   */
  public removeWorkflowId(): void {
    this.stateStoreUtilsService.removeWorkflowId();
  }

  /**
   * removeWorkflowId method is used to remove current workflow id from headers
   * @param flowName of type string, having flow name of repdash
   * @returns void
   */
  public getContentData(pageUrl: string): Observable<Workflow[]> {
    return this.contentProviderService.getContentData(pageUrl).pipe(
      map((contentData: Workflow) => {
        return JSON.parse(contentData[0].workflow);
      })
    );
  }
  /**
   * getSelectedRoute method is used to fetch the selected route on the basis of url
   * @param url of type string, having url of workflow
   * @returns void
   */
  private getSelectedRoute(url: string): Workflow {
    if (!this.workflowInformation) {
      this.workflowInformation = this.stateStoreUtilsService.getWorkflowInformation();
    }
    if (this.workflowInformation) {
      const workFlow: Workflow[] = this.workflowInformation.filter((routeInfo: Workflow) => {
        if (url.indexOf(routeInfo.route) !== -1) {
          return routeInfo;
        }
      });
      if (workFlow && workFlow.length) {
        return workFlow[0];
      }
    }
  }

  /**
   * updateWorkFlowId method is used to set/check the modified repdash pages
   * @param selectedRoute of type Workflow, having selected route info
   * @param type of type string having true/false values as string
   * @returns void
   */
  private updateWorkFlowId(selectedRoute: Workflow, type: string): void {
    let workFlowId: string;
    if (selectedRoute) {
      if (!selectedRoute.isCarryForward || type !== 'Default') {
        workFlowId = this.getWorkFlowInfo(selectedRoute, type);
      }
      const activeTiles = this.workflowCartSupportService.getAllTiles();
      if (workFlowId) {
        if (activeTiles && activeTiles.length >= 1) {
          const allWorkflows = this.getUniqueWorkflowForAllLines(workFlowId);
          this.stateStoreUtilsService.setWorkflowId(allWorkflows);
        } else {
          this.stateStoreUtilsService.setWorkflowId(workFlowId);
        }
      }
    }
  }

  /**
   * getWorkFlowInfo method is used to get the workflow info associated to its type
   * @param selectedRoute of type string, having selectedRoute workflow information
   * @param type of type string having type of the workflow
   * @returns workflowId of type string
   */
  private getWorkFlowInfo(selectedRoute: Workflow, type: string): string {
    const workflowArray = selectedRoute.flowInfo.filter((workflowObj: FlowInfo) => {
      if (workflowObj.type === type) {
        return workflowObj;
      }
    });
    if (workflowArray && workflowArray.length) {
      return workflowArray[0].id;
    }
  }
  /**
   * getUniqueWorkflowForAllLines method is used to fetch workflow information of all lines
   * @param workflowId of type string, having currentWorkflow id
   * @returns workflow ids of all lines
   */
  private getUniqueWorkflowForAllLines(workflowId: string): string {
    // @TODO: need to revisit this logic
    if (!this.firstTileObject) {
      this.workflowMap = [];
      this.firstTileObject = this.setFirstTileWorkflowInformation();
      this.workflowMap.push(this.firstTileObject);
    }
    this.updateWorkflowInformation(workflowId);
    const workflowsForAllLines = this.updateWorkflowForAllLines();
    return workflowsForAllLines;
  }

  /**
   * setFirstTileWorkflowInformation method is used to set tileId of first tile
   * @param flowName of type string,
   * @param data of type string having true/false values as string
   * @returns void
   */
  private setFirstTileWorkflowInformation(): LineWorkflow {
    const firstTileObject: LineWorkflow = {
      tileId: this.workflowCartSupportService.getAllTiles()[0],
      workflowId: this.stateStoreUtilsService.getWorkflowId(),
    };
    return firstTileObject;
  }

  /**
   * updateWorkflowInformation method is used to set/check the modified repdash pages
   * @param flowName of type string, having flow name of repdash
   * @param data of type string having true/false values as string
   * @returns void
   */
  private updateWorkflowInformation(workflowId: string): void {
    const currentTileId = this.stateStoreUtilsService.getFocusedTileId();
    const isLineExisting: LineWorkflow = this.workflowMap.filter((workFlowMap: LineWorkflow) => {
      return workFlowMap.tileId === currentTileId;
    })[0];
    if (isLineExisting) {
      isLineExisting.workflowId = workflowId;
    } else {
      const currentWorkflowObject: LineWorkflow = {
        tileId: currentTileId,
        workflowId: workflowId,
      };
      this.workflowMap.push(currentWorkflowObject);
    }
  }

  /**
   * updateWorkflowForAllLines method is used to set/check the modified repdash pages
   * @param flowName of type string, having flow name of repdash
   * @param data of type string having true/false values as string
   * @returns void
   */
  private updateWorkflowForAllLines(): string {
    let workFlowsForAllLines: string = null;
    this.workflowMap.forEach((workFlowMap: LineWorkflow, index: number) => {
      if (index <= this.workflowMap.length - 1 && workFlowMap.workflowId) {
        workFlowsForAllLines = workFlowMap.workflowId;
      }
    });
    return workFlowsForAllLines;
  }
}
